import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { StickyMobileCTA } from "./components/StickyMobileCTA";

import HomePage from "./pages/HomePage";
import LoanProductPage from "./pages/LoanProductPage";
import LenderDetailPage from "./pages/LenderDetailPage";
import {
  ToolsHub, EMICalculatorPage, EligibilityCalculatorPage, BalanceTransferPage,
  PartPaymentPage, SIPPage, FDRDPage, IncomeTaxPage, CompareLoansPage
} from "./pages/ToolsPages";
import { LearnPage, BlogPostPage } from "./pages/LearnPages";
import { AboutPage, ContactPage, ApplyPage, StaticPage } from "./pages/StaticPages";
import { LoginPage, AuthCallback, ProtectedRoute } from "./pages/AuthPages";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import { AuthProvider } from "./context/AuthContext";

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname, search]);
  return null;
};

const PRIVACY = `OneLoan.in respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard the information you provide on our platform.\n\nWe collect basic personal and financial information such as name, contact details, employment information, income, and other related details only with your explicit consent, in order to match you with appropriate loan products from our partner Banks and NBFCs.\n\nYour data is transmitted via 256-bit SSL encryption and stored securely. We never sell or rent your information to third parties for marketing purposes. Information may be shared with partner lenders only when you authorise an application.\n\nFor any privacy-related queries, please write to privacy@oneloan.in.`;

const TERMS = `By accessing or using OneLoan.in, you agree to comply with and be bound by these Terms & Conditions.\n\nOneLoan.in is a digital loan distribution and facilitation platform. We do not directly lend money. All loans are sanctioned by partner Banks and NBFCs at their sole discretion.\n\nUsers must be at least 18 years old and Indian residents. By using our services, you authorise us to share your information with potential lenders for the purpose of evaluating your application.\n\nWe reserve the right to modify these terms at any time. Continued use of the platform constitutes acceptance of the revised terms.`;

const DISCLAIMER = `OneLoan.in is a loan facilitation and distribution platform. Loan approval, interest rates, loan amount, tenure, and terms are determined solely by the respective Banks, NBFCs, and lending partners. Submission of an application does not guarantee approval. OneLoan does not directly provide loans unless specifically stated. Users should carefully review all terms and conditions before applying.\n\nAll interest rates, processing fees, and other figures shown on this website are indicative only and subject to change at the lender's discretion without prior notice. We recommend verifying the latest terms directly with the lender before finalising any application.`;

function SiteLayout({ children }) {
  return (
    <>
      <Header/>
      <main>{children}</main>
      <Footer/>
      <StickyMobileCTA/>
      <Toaster position="top-right" richColors/>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop/>
          <Routes>
            <Route path="/admin" element={<><AdminPage/><Toaster position="top-right" richColors/></>}/>
            <Route path="/*" element={
              <SiteLayout>
                <Routes>
                  <Route path="/" element={<HomePage/>}/>
                  <Route path="/personal-loan" element={<LoanProductPage type="personal"/>}/>
                  <Route path="/business-loan" element={<LoanProductPage type="business"/>}/>
                  <Route path="/overdraft-loan" element={<LoanProductPage type="overdraft"/>}/>
                  <Route path="/personal-loan/:slug" element={<LenderDetailPage product="personal"/>}/>
                  <Route path="/business-loan/:slug" element={<LenderDetailPage product="business"/>}/>
                  <Route path="/overdraft-loan/:slug" element={<LenderDetailPage product="overdraft"/>}/>
                  <Route path="/tools" element={<ToolsHub/>}/>
                  <Route path="/tools/emi-calculator" element={<EMICalculatorPage/>}/>
                  <Route path="/tools/eligibility-calculator" element={<EligibilityCalculatorPage/>}/>
                  <Route path="/tools/balance-transfer-calculator" element={<BalanceTransferPage/>}/>
                  <Route path="/tools/part-payment-calculator" element={<PartPaymentPage/>}/>
                  <Route path="/tools/sip-calculator" element={<SIPPage/>}/>
                  <Route path="/tools/fd-rd-calculator" element={<FDRDPage/>}/>
                  <Route path="/tools/income-tax-calculator" element={<IncomeTaxPage/>}/>
                  <Route path="/tools/compare-loans" element={<CompareLoansPage/>}/>
                  <Route path="/learn" element={<LearnPage/>}/>
                  <Route path="/learn/:slug" element={<BlogPostPage/>}/>
                  <Route path="/about" element={<AboutPage/>}/>
                  <Route path="/contact" element={<ContactPage/>}/>
                  <Route path="/apply" element={<ApplyPage/>}/>
                  <Route path="/login" element={<LoginPage/>}/>
                  <Route path="/auth/callback" element={<AuthCallback/>}/>
                  <Route path="/profile" element={<ProtectedRoute><ProfilePage/></ProtectedRoute>}/>
                  <Route path="/privacy" element={<StaticPage title="Privacy Policy" body={PRIVACY}/>}/>
                  <Route path="/terms" element={<StaticPage title="Terms & Conditions" body={TERMS}/>}/>
                  <Route path="/disclaimer" element={<StaticPage title="Disclaimer" body={DISCLAIMER}/>}/>
                  <Route path="*" element={<HomePage/>}/>
                </Routes>
              </SiteLayout>
            }/>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
